import type { BillingAddress } from '../shared/address.ts';
import type {
  PaymentAVSResult,
  PaymentCVVResult,
  PaymentFraudScoreResult,
} from '../shared/payment.ts';

// whitelist for billing data to allow it to be sent to the client.
export enum BillingInfoSafeForClientEnum {
  achAccountLastFour = 'achAccountLastFour',
  achAccountNumber = 'achAccountNumber',
  achRoutingNumber = 'achRoutingNumber',
  achAccountName = 'achAccountName',
  addressOption = 'addressOption',
  billingAddress = 'billingAddress',
  billingAddress2 = 'billingAddress2',
  billingCity = 'billingCity',
  billingCountry = 'billingCountry',
  billingPostal = 'billingPostal',
  billingState = 'billingState',
  cardCode = 'cardCode',
  cardExpiration = 'cardExpiration',
  cardNumber = 'cardNumber',
  cardOwnerName = 'cardOwnerName',
  cardType = 'cardType',
  hasMatchingAddress = 'hasMatchingAddress',
  isRecurring = 'isRecurring',
  nickname = 'nickname',
  paymentDay = 'paymentDay',
  paymentMethodId = 'paymentMethodId',
  paymentType = 'paymentType',
}

export enum BillingFieldsBaseEnum {
  HasMatchingAddress = 'hasMatchingAddress',
  // Not yet being used.
  PaymentType = 'paymentType',
  AddressOption = 'addressOption',
}
export enum BillingFieldsCardEnum {
  /**
   * TODO: we do not *ever* want to store the CVV! Instead, this will be sent
   * off to tokenex and we should just store a boolean of `hasCVV`
   */
  CardCode = 'cardCode',
  /**
   * TODO: Expiration dates need to be sent separately to the processing APIs.
   * Does it make more sense to keep these separated here, and then only
   * combine them in the presentation layer?
   */
  CardExpiration = 'cardExpiration',
  CardExpirationMonth = 'CardExpirationMonth',
  CardExpirationYear = 'CardExpirationYear',
  CardNumber = 'cardNumber',
  CardOwnerName = 'cardOwnerName',
  CardType = 'cardType',
  DPMAuthDetails = 'dpmAuthDetails',
  // MerchantOrderNumber = 'merchantOrderNumber',
}

export enum BillingFieldsCardApplePayEnum {
  DPMAuthDetails = 'dpmAuthDetails',
  DPMType = 'dpmType',
}

export enum BillingFieldsACHEnum {
  ACHAccountOwner = 'achAccountName',
  ACHAccountNumber = 'achAccountNumber',
  ACHRoutingNumber = 'achRoutingNumber',
  // ACHAccountType = 'achAccountType',
  ACHAccountNumberLastFour = 'achAccountLastFour',
}

export const BillingFieldsEnum = {
  ...BillingFieldsBaseEnum,
  ...BillingFieldsCardEnum,
  ...BillingFieldsACHEnum,
};

export enum CreditCardEnum {
  NA = 'na',
  Amex = 'american-express',
  Discover = 'discover',
  Mastercard = 'mastercard',
  Visa = 'visa',
}

export enum CheckoutPaymentTypeEnum {
  CreditCard = 'CreditCard',
  ACH = 'ACH',
  ApplePay = 'ApplePay',
  PayPal = 'PayPal',
}

export type PaymentProcessAttempts =
  | {
      -readonly [key in keyof typeof CheckoutPaymentTypeEnum as key]: number;
    }
  | null;

// We simply default new payment methods to saved and any we receive are already saved
// export type CheckoutSaveablePayment = {
//   /**
//    * Is this payment method saved for future use?
//    */
//   isSaved: boolean;
//   /**
//    * Should this payment method be saved for future use?
//    */
//   savePaymentMethod: boolean; // TODO: isSaved and savePaymentMethod seem to be the same thing...
// };

export type CheckoutPaymentCreditCard = {
  addressOption?: string;
  authorizationCode?: string;
  avsResponse: PaymentAVSResult | null;
  cardType: CreditCardEnum;
  cvvResult: PaymentCVVResult | null;
  merchantOrderNumber: string;
  paymentType: CheckoutPaymentTypeEnum.CreditCard;
  responseDate?: string;
  responseReasonCode?: string;
  fraudScoreResult?: PaymentFraudScoreResult | null;
} & {
  // For now this keeps things streamlined. However will need to rethink if
  // fields become optional or have different types
  [key in keyof typeof BillingFieldsCardEnum as Uncapitalize<key>]: string;
};

export type CheckoutPaymentACH = {
  paymentType: CheckoutPaymentTypeEnum.ACH;
  achAccountLastFour: string;
  achAccountName: string;
  achAccountNumber: string;
  achRoutingNumber: string;
  // } & {
  //   // For now this keeps things streamlined. However will need to rethink if
  //   // fields become optional or have different types
  //   [key in keyof typeof BillingFieldsACHEnum as Uncapitalize<key>]: string;
};

export type CheckoutPaymentPayPal = {
  accountEmail: string;
  authorizationId: string;
  payerId: string;
  paymentType: CheckoutPaymentTypeEnum.PayPal;
  // token: string;
  // We will use the orderId as the token in the transaction
  paypalOrderId?: string;
};

export type CheckoutPaymentApplePay = Omit<
  CheckoutPaymentCreditCard,
  'paymentType' | 'fraudScoreResult'
> & {
  paymentType: CheckoutPaymentTypeEnum.ApplePay;
  paymentDetails: ApplePayJS.ApplePayPayment;
  transactionType: number;
  isAuthorized: boolean;
} & {
  [key in keyof typeof BillingFieldsCardApplePayEnum as Uncapitalize<key>]: string;
};

export type CheckoutPayment =
  | CheckoutPaymentCreditCard
  | CheckoutPaymentACH
  | CheckoutPaymentPayPal
  | CheckoutPaymentApplePay;

/**
 * Data from billing fields.
 */
export type CheckoutBillingInfo = CheckoutPayment &
  BillingAddress & {
    hasMatchingAddress?: boolean;
    isRecurring: boolean;
    paymentDay?: number;
    paymentMethodId: string | null;
    paymentType: CheckoutPaymentTypeEnum;
    withNewBillingAddress?: boolean;
    paymentProcessAttempts?: PaymentProcessAttempts;
    paymentAuthDetails?: Record<string, string>;
    nickname?: string;
    // Property that will be added to BillingInfo contract when a logged in supporter selects a payment method. This is necessary for the inforeview or payment method copmonent to properly display and check the proper radio button.
  };

export enum FraudScoreEnabledState {
  Disabled = 'Disabled',
  Enabled = 'Enabled',
  GuestOnly = 'GuestOnly',
}
